import { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router";
import {
  SplitCostingWizardCtx,
  type SplitCostingFailedUploadAttempt,
} from "./SplitCostingWizardContext";
import { useSplitCostingImportInvocation } from "../hooks/useSplitCostingImportInvocation";
import type { MyPayEnvironment } from "../../generated/core-api";
import type { SplitCostingRow, SplitCostingParseError } from "../utils";

export interface SplitCostingWizardProviderProps {
  /** Validated company code from the layout gate. */
  companyCode: string;
  /** Datapay environment from the layout gate. */
  mypayEnvironment: MyPayEnvironment;
  /** Company name from the layout gate. */
  companyName: string;
  /** Notifies the layout so company context cannot change mid-invocation. */
  onBusyChange?: (isBusy: boolean) => void;
  /** Notifies the layout when changing company context would discard a batch. */
  onUncommittedProgressChange?: (hasProgress: boolean) => void;
  /** Coordinates layout-owned confirmations with composer-owned modals. */
  isParentModalOpen?: boolean;
}

/**
 * Provides upload state for the split costing import wizard.
 *
 * Company info is passed in as props from `SplitCostingImportLayout`, which
 * holds the binding via its own local state. Mounting / unmounting this
 * provider (when the company changes or is cleared) resets all upload state
 * automatically — satisfying R-CTX-6.
 *
 * Renders the active wizard screen via `<Outlet />`.
 *
 * Also instantiates the single `useSplitCostingImportInvocation` manager for
 * the whole wizard (validate + commit), rather than letting `ValidateScreen`
 * create its own. A commit can run for a while — the plan calls for
 * navigating to `ResultScreen` (step 6, C4) immediately on submit and polling
 * there. A screen-local instance would be unmounted by that route change,
 * killing the in-flight poll; instantiating it here (a screen-neutral parent)
 * lets both `ValidateScreen` and `ResultScreen` read the same instance via
 * `wizardCtx.manager`. Mirrors `pay-component-import`'s `WizardProvider`,
 * which lifts `useWorkflowCommit` for the same reason.
 */
export function SplitCostingWizardProvider({
  companyCode,
  mypayEnvironment,
  companyName,
  onBusyChange,
  onUncommittedProgressChange,
  isParentModalOpen = false,
}: SplitCostingWizardProviderProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [rawCsvText, setRawCsvText] = useState<string | null>(null);
  const [parsedRows, setParsedRows] = useState<SplitCostingRow[] | null>(null);
  const [parseErrors, setParseErrors] = useState<SplitCostingParseError[]>([]);
  const [failedUploadAttempt, setFailedUploadAttemptState] =
    useState<SplitCostingFailedUploadAttempt | null>(null);
  const manager = useSplitCostingImportInvocation();
  const isBusy = manager.isValidating || manager.isCommitting;
  const hasEffectiveUncommittedProgress =
    manager.hasUncommittedImportProgress || failedUploadAttempt !== null;

  useEffect(() => {
    onBusyChange?.(isBusy);
    return () => onBusyChange?.(false);
  }, [isBusy, onBusyChange]);

  useEffect(() => {
    onUncommittedProgressChange?.(hasEffectiveUncommittedProgress);
    return () => onUncommittedProgressChange?.(false);
  }, [
    hasEffectiveUncommittedProgress,
    onUncommittedProgressChange,
  ]);

  useEffect(() => {
    if (
      manager.commitResult !== null &&
      !manager.hasUncommittedImportProgress
    ) {
      setFailedUploadAttemptState(null);
    }
  }, [manager.commitResult, manager.hasUncommittedImportProgress]);

  const setUploadSuccess = useCallback(
    (
      newFileName: string,
      rows: SplitCostingRow[],
      newFile: File | null = null,
      newRawCsvText: string | null = null
    ) => {
      setFileName(newFileName);
      setFile(newFile);
      setRawCsvText(newRawCsvText);
      setParsedRows(rows);
      setParseErrors([]);
      setFailedUploadAttemptState(null);
    },
    []
  );

  const setUploadErrors = useCallback(
    (
      newFileName: string,
      errors: SplitCostingParseError[],
      newFile: File | null = null,
      newRawCsvText: string | null = null
    ) => {
      setFileName(newFileName);
      setFile(newFile);
      setRawCsvText(newRawCsvText);
      setParsedRows(null);
      setParseErrors(errors);
    },
    []
  );

  const setFailedUploadAttempt = useCallback(
    (
      newFileName: string,
      errors: SplitCostingParseError[],
      newFile: File | null = null,
      newRawCsvText: string | null = null
    ) => {
      setFailedUploadAttemptState({
        fileName: newFileName,
        file: newFile,
        rawCsvText: newRawCsvText,
        parseErrors: errors,
      });
    },
    []
  );

  const clearUpload = useCallback(() => {
    setFileName(null);
    setFile(null);
    setRawCsvText(null);
    setParsedRows(null);
    setParseErrors([]);
    setFailedUploadAttemptState(null);
  }, []);

  const contextValue = useMemo(
    () => ({
      companyCode,
      companyName,
      mypayEnvironment,
      fileName,
      file,
      rawCsvText,
      parsedRows,
      parseErrors,
      failedUploadAttempt,
      isParentModalOpen,
      setUploadSuccess,
      setUploadErrors,
      setFailedUploadAttempt,
      clearUpload,
      manager,
    }),
    [
      companyCode,
      companyName,
      mypayEnvironment,
      fileName,
      file,
      rawCsvText,
      parsedRows,
      parseErrors,
      failedUploadAttempt,
      isParentModalOpen,
      setUploadSuccess,
      setUploadErrors,
      setFailedUploadAttempt,
      clearUpload,
      manager,
    ]
  );

  return (
    <SplitCostingWizardCtx.Provider value={contextValue}>
      <Outlet />
    </SplitCostingWizardCtx.Provider>
  );
}
